import { useState, useEffect } from 'react';

interface TypedConfirmModalProps {
  /** Headline e.g. "12 Tenants suspendieren?". */
  title: string;
  /** Sub-text rendered below the title. */
  description?: string;
  /**
   * Phrase the operator must type verbatim before "Bestaetigen" is
   * enabled. Comparison is case-sensitive — matches the rendered
   * label exactly so SUSPEND and Suspend aren't interchangeable.
   */
  confirmPhrase: string;
  /** Label for the destructive button. */
  confirmLabel: string;
  /** Affected slugs — rendered as a preview list so the operator
   *  can sanity-check the selection before typing the phrase. */
  affectedSlugs: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Typed-confirmation modal for destructive bulk operations. We
 * gate bulk-suspend / bulk-decommission on >5 tenants behind a
 * phrase the operator must type — protects against accidental
 * misclicks (e.g. wiping every Enterprise tenant during a partner
 * maintenance window). The phrase is rendered, not hidden, so
 * there's no "guess the magic word" UX trap.
 */
export function TypedConfirmModal({
  title,
  description,
  confirmPhrase,
  confirmLabel,
  affectedSlugs,
  onConfirm,
  onCancel,
}: TypedConfirmModalProps) {
  const [typed, setTyped] = useState('');

  // Reset the typed phrase whenever the required phrase changes —
  // e.g. operator opens a fresh decommission dialog after a prior
  // suspend dialog. Without this, an old "SUSPEND" entry would
  // remain visible and look like the wrong-phrase case.
  useEffect(() => {
    setTyped('');
  }, [confirmPhrase]);

  const matches = typed === confirmPhrase;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-lg">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-red-700">{title}</h3>
          {description && <p className="text-sm text-slate-600 mt-1">{description}</p>}
        </div>
        <div className="p-4 space-y-3">
          <div>
            <p className="text-xs text-slate-500 mb-1">Betroffene Tenants ({affectedSlugs.length}):</p>
            <div className="max-h-32 overflow-y-auto bg-slate-50 rounded p-2 text-xs font-mono">
              {affectedSlugs.join(', ')}
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">
              Tippe <code className="bg-slate-100 px-1 py-0.5 rounded text-red-700">{confirmPhrase}</code> zur Bestaetigung:
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              className="w-full px-2 py-1 rounded border font-mono"
              placeholder={confirmPhrase}
            />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1 rounded border">
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches}
            className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
